import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import locationService from '../services/locationService'

const RegionalSearchForm = ({ 
  onRegionalConfigChange, 
  onPrequalificationChange,
  regionalConfig,
  prequalificationConfig,
  searchQuery = ''  // NEU: Suchbegriff von oben
}) => {
  const [states, setStates] = useState([])
  const [availableCities, setAvailableCities] = useState([])
  const [selectedCities, setSelectedCities] = useState([])
  const [loadingStates, setLoadingStates] = useState(false)
  const [loadingCities, setLoadingCities] = useState(false)
  const [showPrequalification, setShowPrequalification] = useState(false)
  const [importStatus, setImportStatus] = useState(null)
  const [citySearchQuery, setCitySearchQuery] = useState('')
  
  // NEU: Search string format options
  const [includeZip, setIncludeZip] = useState(true)
  const [includeCity, setIncludeCity] = useState(true)

  // Load federal states on mount
  useEffect(() => {
    loadStates()
  }, [])

  const loadStates = async () => {
    setLoadingStates(true)
    try {
      console.log('[Regional Search] Loading federal states...')
      const data = await locationService.getFederalStates()
      console.log('[Regional Search] Received data:', data)
      
      if (data && Array.isArray(data) && data.length > 0) {
        setStates(data)
        console.log(`✅ ${data.length} Bundesländer erfolgreich geladen`)
      } else {
        console.error('[Regional Search] Invalid data received:', data)
        toast.error('Keine Bundesländer gefunden')
      }
    } catch (error) {
      console.error('[Regional Search] Error loading states:', error)
      console.error('[Regional Search] Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status
      })
      
      // Detaillierte Fehlermeldung
      if (error.response?.status === 404) {
        toast.error('Bundesländer-API nicht gefunden (404)')
      } else if (error.response?.status === 500) {
        toast.error('Backend-Fehler beim Laden der Bundesländer (500)')
      } else if (error.message) {
        toast.error(`Fehler: ${error.message}`)
      } else {
        toast.error('Fehler beim Laden der Bundesländer')
      }
    } finally {
      setLoadingStates(false)
    }
  }

  const handleImportZipCodes = async () => {
    const toastId = toast.loading('Importiere PLZ-Daten...')
    try {
      const stats = await locationService.importZipCodes()
      setImportStatus(stats)
      toast.success(`Import erfolgreich! ${stats.imported} Einträge importiert.`, { id: toastId })
      
      // Reload states
      await loadStates()
    } catch (error) {
      console.error('Import failed:', error)
      toast.error('Import fehlgeschlagen: ' + error.message, { id: toastId })
    }
  }

  const handleStateChange = async (stateCode) => {
    onRegionalConfigChange({
      ...regionalConfig,
      state_code: stateCode,
      cities: [],
      include_zip: includeZip,
      include_city: includeCity
    })
    
    setSelectedCities([])
    
    if (stateCode) {
      await loadCities(stateCode)
    } else {
      setAvailableCities([])
    }
  }
  
  // NEU: Update config when search format changes
  useEffect(() => {
    if (regionalConfig?.state_code) {
      onRegionalConfigChange({
        ...regionalConfig,
        include_zip: includeZip,
        include_city: includeCity
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeZip, includeCity])

  const loadCities = async (stateCode) => {
    setLoadingCities(true)
    try {
      // Load ALL cities for the selected state (no limit)
      const cities = await locationService.getCitiesByState(stateCode, null, 'mixed')
      setAvailableCities(cities)
      console.log(`✅ ${cities.length} Städte für ${stateCode} geladen`)
    } catch (error) {
      console.error('Failed to load cities:', error)
      toast.error('Fehler beim Laden der Städte')
    } finally {
      setLoadingCities(false)
    }
  }

  const handleCityToggle = (city) => {
    const isSelected = selectedCities.find(c => c.zip === city.zip && c.city === city.city)
    
    let newSelected
    if (isSelected) {
      newSelected = selectedCities.filter(c => !(c.zip === city.zip && c.city === city.city))
    } else {
      newSelected = [...selectedCities, { zip: city.zip, city: city.city }]
    }
    
    setSelectedCities(newSelected)
    onRegionalConfigChange({
      ...regionalConfig,
      cities: newSelected
    })
  }

  const handleSelectAll = () => {
    const filteredCities = getFilteredCities()
    const citiesToSelect = filteredCities.map(c => ({ zip: c.zip, city: c.city }))
    setSelectedCities(citiesToSelect)
    onRegionalConfigChange({
      ...regionalConfig,
      cities: citiesToSelect
    })
  }

  const handleDeselectAll = () => {
    setSelectedCities([])
    onRegionalConfigChange({
      ...regionalConfig,
      cities: []
    })
  }

  // Filter cities based on search query
  const getFilteredCities = () => {
    if (!citySearchQuery.trim()) {
      return availableCities
    }
    
    const query = citySearchQuery.toLowerCase()
    return availableCities.filter(city => 
      city.city.toLowerCase().includes(query) || 
      city.zip.includes(query)
    )
  }

  const loadExampleCriteria = (type) => {
    const examples = {
      food_bank: {
        inclusion: 'Die Seite muss eine echte Essensausgabe (Tafel) vor Ort beschreiben mit konkreter Adresse, Öffnungszeiten und Kontaktinformationen. Es muss ein aktives Angebot sein, wo bedürftige Menschen Lebensmittel erhalten können.',
        exclusion: 'Keine Zeitungsartikel über Tafeln, keine TV-Berichte, keine reinen Informationsseiten ohne konkretes Angebot, keine Spendenaufrufe ohne Ausgabestelle, keine historischen Berichte.'
      },
      shelter: {
        inclusion: 'Die Seite muss eine Notunterkunft oder Obdachlosenunterkunft beschreiben mit Adresse, Aufnahmekriterien, Kontaktmöglichkeiten und Informationen zur Verfügbarkeit.',
        exclusion: 'Keine Zeitungsberichte, keine Statistiken über Obdachlosigkeit, keine historischen Artikel, keine reinen Beratungsstellen ohne Unterkunft.'
      }
    }
    
    const example = examples[type]
    if (example) {
      onPrequalificationChange({
        ...prequalificationConfig,
        inclusion_criteria: example.inclusion,
        exclusion_criteria: example.exclusion
      })
      toast.success('Beispielkriterien geladen')
    }
  }

  return (
    <div className="space-y-6">
      {/* Info: Bundesländer sind fest hinterlegt */}
      {states.length === 0 && !loadingStates && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
          <div className="flex items-center">
            <i className="fa-solid fa-exclamation-triangle text-red-500 text-xl mr-3"></i>
            <div>
              <h3 className="text-sm font-semibold text-red-800 mb-1">
                Bundesländer konnten nicht geladen werden
              </h3>
              <p className="text-sm text-red-700">
                Die Bundesländer sind fest in der Datenbank hinterlegt. Bitte Backend-Admin kontaktieren.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bundesland Selection */}
      <div>
        <label className="block mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-dark-300">
              Bundesland auswählen <span className="text-primary-500">*</span>
            </span>
          </div>
          <select
            value={regionalConfig.state_code || ''}
            onChange={(e) => handleStateChange(e.target.value)}
            className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
            disabled={loadingStates}
          >
            <option value="">Bundesland wählen...</option>
            {states.map(state => (
              <option key={state.code} value={state.code}>
                {state.name} ({state.code}) - {state.cities_count} Orte
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Cities Selection */}
      {regionalConfig.state_code && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-bold text-dark-300">
              Städte auswählen ({selectedCities.length} ausgewählt)
            </label>
            <div className="space-x-2">
              <button
                type="button"
                onClick={handleSelectAll}
                className="px-3 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600 transition-all"
              >
                Alle auswählen
              </button>
              <button
                type="button"
                onClick={handleDeselectAll}
                className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-all"
              >
                Alle abwählen
              </button>
            </div>
          </div>
          
          {loadingCities ? (
            <div className="flex items-center justify-center p-8">
              <i className="fa-solid fa-spinner fa-spin text-primary-500 text-2xl"></i>
            </div>
          ) : (
            <>
              {/* City Search Box */}
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fa-solid fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={citySearchQuery}
                  onChange={(e) => setCitySearchQuery(e.target.value)}
                  placeholder="Stadt suchen (Name oder PLZ)..."
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
                {citySearchQuery && (
                  <button
                    type="button"
                    onClick={() => setCitySearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <i className="fa-solid fa-times text-gray-400 hover:text-gray-600"></i>
                  </button>
                )}
              </div>
              
              {/* Filtered Results Count */}
              {citySearchQuery && (
                <div className="text-sm text-gray-600 mb-2">
                  <i className="fa-solid fa-filter mr-1"></i>
                  {getFilteredCities().length} von {availableCities.length} Städten gefunden
                </div>
              )}
              
              <div className="max-h-64 overflow-y-auto border-2 border-light-200/30 rounded-lg p-3">
                {getFilteredCities().length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {getFilteredCities().map((city) => (
                      <label
                        key={`${city.zip}-${city.city}`}
                        className={`flex items-center p-2 rounded cursor-pointer transition-all ${
                          selectedCities.find(c => c.zip === city.zip && c.city === city.city)
                            ? 'bg-primary-100 border-primary-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedCities.find(c => c.zip === city.zip && c.city === city.city)}
                          onChange={() => handleCityToggle(city)}
                          className="mr-2"
                        />
                        <span className="text-sm text-dark-300">
                          <span className="font-semibold">{city.city}</span>
                          <span className="text-gray-500 ml-1">({city.zip})</span>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <i className="fa-solid fa-search text-3xl mb-2 block"></i>
                    <p className="text-sm">Keine Städte gefunden für "{citySearchQuery}"</p>
                    <button
                      type="button"
                      onClick={() => setCitySearchQuery('')}
                      className="mt-2 text-xs text-primary-600 hover:text-primary-700 underline"
                    >
                      Suche zurücksetzen
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Search String Format Options */}
      {regionalConfig.state_code && (
        <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">
            <i className="fa-solid fa-cog mr-2"></i>
            Suchstring-Format
          </h4>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={includeZip}
                onChange={(e) => setIncludeZip(e.target.checked)}
                className="mr-2 h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">PLZ einbeziehen</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={includeCity}
                onChange={(e) => setIncludeCity(e.target.checked)}
                className="mr-2 h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Ort einbeziehen</span>
            </label>
          </div>
          {!includeZip && !includeCity && (
            <p className="text-xs text-red-600 mt-2">
              <i className="fa-solid fa-exclamation-triangle mr-1"></i>
              Warnung: Mindestens PLZ oder Ort sollte ausgewählt sein
            </p>
          )}
        </div>
      )}

      {/* Search Preview */}
      {selectedCities.length > 0 && (
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">
            <i className="fa-solid fa-eye mr-2"></i>
            Vorschau der Suchanfragen
          </h4>
          <div className="max-h-32 overflow-y-auto">
            <ul className="text-sm text-blue-700 space-y-1">
              {selectedCities.slice(0, 5).map((city, idx) => {
                const searchParts = [
                  searchQuery || '[Suchbegriff]',
                  includeZip ? city.zip : null,
                  includeCity ? city.city : null
                ].filter(Boolean)
                
                return (
                  <li key={idx}>
                    → {searchParts.join(' ')}
                  </li>
                )
              })}
              {selectedCities.length > 5 && (
                <li className="font-semibold">
                  ... und {selectedCities.length - 5} weitere
                </li>
              )}
            </ul>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Insgesamt werden <strong>{selectedCities.length}</strong> Suchanfragen durchgeführt
          </p>
        </div>
      )}

      {/* Prequalification Section */}
      <div className="border-t-2 border-light-200/30 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-dark-300">
            <i className="fa-solid fa-filter mr-2"></i>
            KI-Präqualifizierung (Optional)
          </h3>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={prequalificationConfig.enabled}
              onChange={(e) => {
                onPrequalificationChange({
                  ...prequalificationConfig,
                  enabled: e.target.checked
                })
                setShowPrequalification(e.target.checked)
              }}
              className="mr-2"
            />
            <span className="text-sm text-dark-300">Aktivieren</span>
          </label>
        </div>

        {prequalificationConfig.enabled && (
          <div className="space-y-4">
            <div className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4">
              <p className="text-sm text-green-800 mb-2">
                <i className="fa-solid fa-info-circle mr-2"></i>
                Die KI filtert automatisch Suchergebnisse basierend auf Ihren Kriterien.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => loadExampleCriteria('food_bank')}
                  className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Essensausgabe Beispiel
                </button>
                <button
                  type="button"
                  onClick={() => loadExampleCriteria('shelter')}
                  className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Notunterkunft Beispiel
                </button>
              </div>
            </div>

            <div>
              <label className="block mb-2">
                <span className="text-sm font-bold text-dark-300">
                  Einschlusskriterien (Was MUSS vorhanden sein)
                </span>
                <textarea
                  value={prequalificationConfig.inclusion_criteria}
                  onChange={(e) => onPrequalificationChange({
                    ...prequalificationConfig,
                    inclusion_criteria: e.target.value
                  })}
                  rows="3"
                  placeholder="z.B. 'Die Seite muss eine echte Essensausgabe vor Ort beschreiben...'"
                  className="w-full mt-1 px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200 text-sm"
                />
              </label>
            </div>

            <div>
              <label className="block mb-2">
                <span className="text-sm font-bold text-dark-300">
                  Ausschlusskriterien (Was darf NICHT vorhanden sein)
                </span>
                <textarea
                  value={prequalificationConfig.exclusion_criteria}
                  onChange={(e) => onPrequalificationChange({
                    ...prequalificationConfig,
                    exclusion_criteria: e.target.value
                  })}
                  rows="3"
                  placeholder="z.B. 'Keine Zeitungsberichte, keine TV-Berichte...'"
                  className="w-full mt-1 px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200 text-sm"
                />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default RegionalSearchForm
